/**
 * PersonaViewer Component
 *
 * Full persona display with tabs for different views:
 * - Formatted View: Structured sections with confidence indicators
 * - JSON: Raw JSON data
 * - YAML: YAML format
 *
 * Includes copy and export functionality.
 */

import { useState } from 'react'
import {
  Copy,
  Download,
  MessageSquare,
  Brain,
  Heart,
  Users,
  Lightbulb,
  AlertCircle,
  CheckCircle,
  Info,
} from 'lucide-react'
import { Persona } from '@/hooks/usePersonas'
import { ModeBadge } from './PersonaModeSelector'

interface PersonaViewerProps {
  persona: Persona
  onExport?: (format: 'json' | 'yaml' | 'markdown') => void
}

type ViewTab = 'formatted' | 'json' | 'yaml'

export default function PersonaViewer({ persona, onExport }: PersonaViewerProps) {
  const [activeTab, setActiveTab] = useState<ViewTab>('formatted')
  const [copiedSection, setCopiedSection] = useState<string | null>(null)

  const copyToClipboard = async (text: string, section?: string) => {
    try {
      await navigator.clipboard.writeText(text)
      if (section) {
        setCopiedSection(section)
        setTimeout(() => setCopiedSection(null), 2000)
      }
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const handleExport = (format: 'json' | 'yaml' | 'markdown') => {
    let content: string
    let filename: string
    let mimeType: string

    switch (format) {
      case 'json':
        content = JSON.stringify(persona, null, 2)
        filename = `persona-${persona.persona_id}.json`
        mimeType = 'application/json'
        break
      case 'yaml':
        content = toYaml(persona)
        filename = `persona-${persona.persona_id}.yaml`
        mimeType = 'text/yaml'
        break
      case 'markdown':
        content = toMarkdown(persona)
        filename = `persona-${persona.persona_id}.md`
        mimeType = 'text/markdown'
        break
    }

    const blob = new Blob([content], { type: mimeType })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
    URL.revokeObjectURL(url)

    onExport?.(format)
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold text-gray-900">Persona Details</h3>
            <ModeBadge mode={persona.sensitivity_mode} />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => copyToClipboard(JSON.stringify(persona, null, 2))}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
            >
              <Copy className="w-4 h-4" />
              Copy All
            </button>
            <div className="relative group">
              <button className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-personic-600 text-white hover:bg-personic-700 rounded-md transition-colors">
                <Download className="w-4 h-4" />
                Export
              </button>
              <div className="absolute right-0 mt-1 w-32 bg-white rounded-md shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                <button
                  onClick={() => handleExport('json')}
                  className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  JSON
                </button>
                <button
                  onClick={() => handleExport('yaml')}
                  className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  YAML
                </button>
                <button
                  onClick={() => handleExport('markdown')}
                  className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  Markdown
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mt-4">
          {(['formatted', 'json', 'yaml'] as ViewTab[]).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                activeTab === tab
                  ? 'bg-white text-personic-700 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              {tab === 'formatted' ? 'Formatted View' : tab.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {activeTab === 'formatted' && (
          <FormattedView
            persona={persona}
            onCopySection={copyToClipboard}
            copiedSection={copiedSection}
          />
        )}
        {activeTab === 'json' && (
          <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
            {JSON.stringify(persona, null, 2)}
          </pre>
        )}
        {activeTab === 'yaml' && (
          <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
            {toYaml(persona)}
          </pre>
        )}
      </div>
    </div>
  )
}

// =============================================================================
// FORMATTED VIEW
// =============================================================================

function FormattedView({
  persona,
  onCopySection,
  copiedSection,
}: {
  persona: Persona
  onCopySection: (text: string, section: string) => void
  copiedSection: string | null
}) {
  return (
    <div className="space-y-6">
      {/* Compliance Notice */}
      {persona.compliance_notice && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-amber-800">Compliance Notice</h4>
              <p className="text-sm text-amber-700 mt-1">{persona.compliance_notice}</p>
            </div>
          </div>
        </div>
      )}

      {/* Communication Style */}
      <SectionCard
        icon={<MessageSquare className="w-5 h-5" />}
        title="Communication Style"
        confidence={getConfidenceForSignal(persona, 'communication')}
        onCopy={() =>
          onCopySection(
            JSON.stringify(persona.communication_briefing, null, 2),
            'communication'
          )
        }
        copied={copiedSection === 'communication'}
      >
        <p className="text-gray-700 mb-4">{persona.communication_briefing.summary}</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <AttributeItem label="Formality" value={persona.communication_briefing.formality} />
          <AttributeItem label="Verbosity" value={persona.communication_briefing.verbosity} />
          <AttributeItem label="Directness" value={persona.communication_briefing.directness} />
          <AttributeItem
            label="Technical Depth"
            value={persona.communication_briefing.technical_depth}
          />
          {persona.communication_briefing.emotional_expressiveness && (
            <AttributeItem
              label="Emotional Expression"
              value={persona.communication_briefing.emotional_expressiveness}
            />
          )}
          {persona.communication_briefing.humor_appreciation && (
            <AttributeItem
              label="Humor Appreciation"
              value={persona.communication_briefing.humor_appreciation}
            />
          )}
        </div>
      </SectionCard>

      {/* Cognitive Style / Personality */}
      <SectionCard
        icon={<Brain className="w-5 h-5" />}
        title="Cognitive Style"
        confidence={getConfidenceForSignal(persona, 'personality')}
        onCopy={() =>
          onCopySection(JSON.stringify(persona.personality_context, null, 2), 'personality')
        }
        copied={copiedSection === 'personality'}
      >
        {persona.personality_context.included ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {persona.personality_context.openness && (
              <AttributeItem label="Openness" value={persona.personality_context.openness} />
            )}
            {persona.personality_context.conscientiousness && (
              <AttributeItem
                label="Conscientiousness"
                value={persona.personality_context.conscientiousness}
              />
            )}
            {persona.personality_context.extraversion && (
              <AttributeItem
                label="Extraversion"
                value={persona.personality_context.extraversion}
              />
            )}
            {persona.personality_context.agreeableness && (
              <AttributeItem
                label="Agreeableness"
                value={persona.personality_context.agreeableness}
              />
            )}
            {persona.personality_context.neuroticism && (
              <AttributeItem
                label="Neuroticism"
                value={persona.personality_context.neuroticism}
              />
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2 text-gray-500">
            <Info className="w-4 h-4" />
            <span className="text-sm">
              {persona.personality_context.note || 'Personality dimensions not included in this mode'}
            </span>
          </div>
        )}
      </SectionCard>

      {/* Emotional Context */}
      <SectionCard
        icon={<Heart className="w-5 h-5" />}
        title="Emotional Patterns"
        confidence={getConfidenceForSignal(persona, 'emotional')}
        onCopy={() =>
          onCopySection(JSON.stringify(persona.emotional_context, null, 2), 'emotional')
        }
        copied={copiedSection === 'emotional'}
      >
        {persona.emotional_context.included ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {persona.emotional_context.baseline_tone && (
              <AttributeItem
                label="Baseline Tone"
                value={persona.emotional_context.baseline_tone}
              />
            )}
            {persona.emotional_context.stress_response && (
              <AttributeItem
                label="Stress Response"
                value={persona.emotional_context.stress_response}
              />
            )}
            {persona.emotional_context.trust_trajectory && (
              <AttributeItem
                label="Trust Trajectory"
                value={persona.emotional_context.trust_trajectory}
              />
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2 text-gray-500">
            <Info className="w-4 h-4" />
            <span className="text-sm">
              {persona.emotional_context.note || 'Emotional patterns not included in this mode'}
            </span>
          </div>
        )}
      </SectionCard>

      {/* Behavioral Patterns */}
      {persona.behavioral_context && persona.behavioral_context.included && (
        <SectionCard
          icon={<Users className="w-5 h-5" />}
          title="Behavioral Patterns"
          confidence={getConfidenceForSignal(persona, 'behavioral')}
          onCopy={() =>
            onCopySection(JSON.stringify(persona.behavioral_context, null, 2), 'behavioral')
          }
          copied={copiedSection === 'behavioral'}
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {persona.behavioral_context.collaboration_style && (
              <AttributeItem
                label="Collaboration"
                value={persona.behavioral_context.collaboration_style}
              />
            )}
            {persona.behavioral_context.feedback_receptivity && (
              <AttributeItem
                label="Feedback Receptivity"
                value={persona.behavioral_context.feedback_receptivity}
              />
            )}
            {persona.behavioral_context.decision_pattern && (
              <AttributeItem
                label="Decision Pattern"
                value={persona.behavioral_context.decision_pattern}
              />
            )}
            {persona.behavioral_context.learning_style && (
              <AttributeItem
                label="Learning Style"
                value={persona.behavioral_context.learning_style}
              />
            )}
          </div>
        </SectionCard>
      )}

      {/* Adaptation Hints */}
      {persona.adaptation_hints && persona.adaptation_hints.length > 0 && (
        <SectionCard
          icon={<Lightbulb className="w-5 h-5" />}
          title="Adaptation Hints"
          onCopy={() =>
            onCopySection(persona.adaptation_hints.join('\n'), 'hints')
          }
          copied={copiedSection === 'hints'}
        >
          <ul className="space-y-2">
            {persona.adaptation_hints.map((hint, index) => (
              <li key={index} className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-700">{hint}</span>
              </li>
            ))}
          </ul>
        </SectionCard>
      )}

      {/* Confidence Metadata */}
      <SectionCard
        icon={<Info className="w-5 h-5" />}
        title="Confidence Information"
        onCopy={() =>
          onCopySection(JSON.stringify(persona.confidence_context, null, 2), 'confidence')
        }
        copied={copiedSection === 'confidence'}
      >
        <div className="space-y-3">
          <div className="flex items-center gap-4">
            <ConfidenceBadge level={persona.confidence_context.overall_confidence} />
            <span className="text-sm text-gray-600">
              Profile Maturity: {persona.confidence_context.profile_maturity}
            </span>
            <span className="text-sm text-gray-600">
              Observations: {persona.confidence_context.observation_count}
            </span>
          </div>
          <p className="text-sm text-gray-600">{persona.confidence_context.recommendation}</p>
        </div>
      </SectionCard>
    </div>
  )
}

// =============================================================================
// HELPER COMPONENTS
// =============================================================================

function SectionCard({
  icon,
  title,
  children,
  confidence,
  onCopy,
  copied,
}: {
  icon: React.ReactNode
  title: string
  children: React.ReactNode
  confidence?: string
  onCopy: () => void
  copied: boolean
}) {
  return (
    <div className="border border-gray-200 rounded-lg">
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-personic-600">{icon}</span>
          <h4 className="font-medium text-gray-900">{title}</h4>
          {confidence && <ConfidenceBadge level={confidence} small />}
        </div>
        <button
          onClick={onCopy}
          className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
        >
          {copied ? (
            <>
              <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
              Copied
            </>
          ) : (
            <>
              <Copy className="w-3.5 h-3.5" />
              Copy
            </>
          )}
        </button>
      </div>
      <div className="p-4">{children}</div>
    </div>
  )
}

function AttributeItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-gray-500 uppercase tracking-wide">{label}</dt>
      <dd className="mt-0.5 text-sm font-medium text-gray-900 capitalize">{value}</dd>
    </div>
  )
}

function ConfidenceBadge({ level, small = false }: { level: string; small?: boolean }) {
  const colors: Record<string, string> = {
    high: 'bg-emerald-100 text-emerald-700',
    moderate: 'bg-amber-100 text-amber-700',
    low: 'bg-orange-100 text-orange-700',
    insufficient: 'bg-red-100 text-red-700',
  }

  return (
    <span
      className={`inline-flex items-center rounded ${colors[level] || 'bg-gray-100 text-gray-700'} ${
        small ? 'px-1.5 py-0.5 text-xs' : 'px-2 py-1 text-sm'
      } font-medium`}
    >
      {level}
    </span>
  )
}

// =============================================================================
// HELPERS
// =============================================================================

function getConfidenceForSignal(persona: Persona, signal: string): string | undefined {
  const highConfidence = persona.confidence_context.high_confidence_signals || []
  const lowConfidence = persona.confidence_context.low_confidence_signals || []

  if (highConfidence.some((s) => s.includes(signal))) return 'high'
  if (lowConfidence.some((s) => s.includes(signal))) return 'low'
  return undefined
}

function toYaml(obj: unknown, indent = 0): string {
  const spaces = '  '.repeat(indent)

  if (obj === null || obj === undefined) {
    return 'null'
  }

  if (typeof obj === 'string') {
    if (obj.includes('\n') || obj.includes(':')) {
      return `|\n${obj
        .split('\n')
        .map((line) => spaces + '  ' + line)
        .join('\n')}`
    }
    return obj
  }

  if (typeof obj === 'number' || typeof obj === 'boolean') {
    return String(obj)
  }

  if (Array.isArray(obj)) {
    if (obj.length === 0) return '[]'
    return obj.map((item) => `${spaces}- ${toYaml(item, indent + 1)}`).join('\n')
  }

  if (typeof obj === 'object') {
    const entries = Object.entries(obj)
    if (entries.length === 0) return '{}'
    return entries
      .map(([key, value]) => {
        const yamlValue = toYaml(value, indent + 1)
        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          return `${spaces}${key}:\n${yamlValue}`
        }
        return `${spaces}${key}: ${yamlValue}`
      })
      .join('\n')
  }

  return String(obj)
}

function toMarkdown(persona: Persona): string {
  const lines: string[] = []

  lines.push(`# Persona: ${persona.persona_id}`)
  lines.push('')
  lines.push(`**Product Context:** ${persona.product_context}`)
  lines.push(`**Sensitivity Mode:** ${persona.sensitivity_mode}`)
  lines.push(`**Generated:** ${new Date(persona.generated_at).toLocaleString()}`)
  lines.push('')

  if (persona.compliance_notice) {
    lines.push('> **Compliance Notice:** ' + persona.compliance_notice)
    lines.push('')
  }

  lines.push('## Communication Style')
  lines.push('')
  lines.push(persona.communication_briefing.summary)
  lines.push('')
  lines.push(`- **Formality:** ${persona.communication_briefing.formality}`)
  lines.push(`- **Verbosity:** ${persona.communication_briefing.verbosity}`)
  lines.push(`- **Directness:** ${persona.communication_briefing.directness}`)
  lines.push(`- **Technical Depth:** ${persona.communication_briefing.technical_depth}`)
  lines.push('')

  if (persona.personality_context.included) {
    lines.push('## Cognitive Style')
    lines.push('')
    if (persona.personality_context.openness)
      lines.push(`- **Openness:** ${persona.personality_context.openness}`)
    if (persona.personality_context.conscientiousness)
      lines.push(`- **Conscientiousness:** ${persona.personality_context.conscientiousness}`)
    if (persona.personality_context.extraversion)
      lines.push(`- **Extraversion:** ${persona.personality_context.extraversion}`)
    if (persona.personality_context.agreeableness)
      lines.push(`- **Agreeableness:** ${persona.personality_context.agreeableness}`)
    if (persona.personality_context.neuroticism)
      lines.push(`- **Neuroticism:** ${persona.personality_context.neuroticism}`)
    lines.push('')
  }

  if (persona.adaptation_hints && persona.adaptation_hints.length > 0) {
    lines.push('## Adaptation Hints')
    lines.push('')
    persona.adaptation_hints.forEach((hint) => {
      lines.push(`- ${hint}`)
    })
    lines.push('')
  }

  lines.push('## Confidence')
  lines.push('')
  lines.push(`**Overall:** ${persona.confidence_context.overall_confidence}`)
  lines.push('')
  lines.push(persona.confidence_context.recommendation)

  return lines.join('\n')
}
